import React, { useEffect } from "react"
import { useSearchParams } from "react-router-dom"

export interface CareerFilterContextValue {
	activeCountyIds?: string[]
	toggleActiveCounty: (id: string) => void
	activeJobTypeIds: string[]
	toggleActiveJobType: (id: string) => void
	activeEmploymentTypeIds: string[]
	toggleActiveEmploymentType: (id: string) => void
}

export const CareerFilterContext =
	React.createContext<CareerFilterContextValue>({
		activeCountyIds: [],
		toggleActiveCounty: (id: string) => null,
		activeJobTypeIds: [],
		toggleActiveJobType: (id: string) => null,
		activeEmploymentTypeIds: [],
		toggleActiveEmploymentType: (id: string) => null,
	})

interface CareerFilterContextProviderProps {
	countyFilter: CareerFilterValue[]
	jobTypeFilter: CareerFilterValue[]
	employmentTypeFilter: CareerFilterValue[]
	children?: React.ReactNode
}

export const CareerFilterContextProvider: React.FC<CareerFilterContextProviderProps> =
	({ countyFilter, jobTypeFilter, employmentTypeFilter, children }) => {
		const getActiveIdsFromSearchParams = (
			name: string,
			validOptions: string[]
		) => {
			const [searchParams] = useSearchParams()
			const value = searchParams.get(name)
			if (!value) return []

			const values = value.split(",")

			// Filter out invalid options
			return values.filter((v) => validOptions.includes(v))
		}

		const [activeCountyIds, setActiveCountyIds] = React.useState(
			getActiveIdsFromSearchParams(
				"county",
				countyFilter.map((cf) => cf.id)
			)
		)
		const [activeJobTypeIds, setActiveJobTypeIds] = React.useState(
			getActiveIdsFromSearchParams(
				"jobType",
				jobTypeFilter.map((jtf) => jtf.id)
			)
		)
		const [activeEmploymentTypeIds, setActiveEmploymentTypeIds] =
			React.useState(
				getActiveIdsFromSearchParams(
					"employmentType",
					employmentTypeFilter.map((jtf) => jtf.id)
				)
			)

		const toggleActiveCounty = (id: string) => {
			if (activeCountyIds.includes(id)) {
				// Remove id
				setActiveCountyIds(
					activeCountyIds.filter((activeId) => activeId !== id)
				)
			} else {
				// Add id
				setActiveCountyIds([...activeCountyIds, id])
			}
		}

		const toggleActiveJobType = (id: string) => {
			if (activeJobTypeIds.includes(id)) {
				// Remove id
				setActiveJobTypeIds(
					activeJobTypeIds.filter((activeId) => activeId !== id)
				)
			} else {
				// Add id
				setActiveJobTypeIds([...activeJobTypeIds, id])
			}
		}

		const toggleActiveEmploymentType = (id: string) => {
			if (activeEmploymentTypeIds.includes(id)) {
				// Remove id
				setActiveEmploymentTypeIds(
					activeEmploymentTypeIds.filter((activeId) => activeId !== id)
				)
			} else {
				// Add id
				setActiveEmploymentTypeIds([...activeEmploymentTypeIds, id])
			}
		}

		useEffect(() => {
			const url = new URL(window.location.href)
			if (!activeCountyIds.length) {
				url.searchParams.delete("county")
			} else {
				url.searchParams.set("county", activeCountyIds.join(","))
			}

			window.history.replaceState({}, "", url)
		}, [activeCountyIds])

		useEffect(() => {
			const url = new URL(window.location.href)
			if (!activeJobTypeIds.length) {
				url.searchParams.delete("jobType")
			} else {
				url.searchParams.set("jobType", activeJobTypeIds.join(","))
			}

			window.history.replaceState({}, "", url)
		}, [activeJobTypeIds])

		useEffect(() => {
			const url = new URL(window.location.href)
			if (!activeEmploymentTypeIds.length) {
				url.searchParams.delete("employmentType")
			} else {
				url.searchParams.set(
					"employmentType",
					activeEmploymentTypeIds.join(",")
				)
			}

			window.history.replaceState({}, "", url)
		}, [activeEmploymentTypeIds])

		return (
			<CareerFilterContext.Provider
				value={{
					activeCountyIds,
					toggleActiveCounty,
					activeJobTypeIds,
					toggleActiveJobType,
					activeEmploymentTypeIds,
					toggleActiveEmploymentType,
				}}
			>
				{children}
			</CareerFilterContext.Provider>
		)
	}
